import type { Connection, EdgeChange, NodeChange } from '@xyflow/react';
import { addEdge, applyEdgeChanges, applyNodeChanges } from '@xyflow/react';
import { temporal } from 'zundo';
import { create } from 'zustand';

import type { WorkflowEdge, WorkflowNode } from '../types';

interface WorkflowStoreState {
  workflowId: string | null;
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
  selectedNodeId: string | null;
  schemaVersion: number;
  isDirty: boolean;

  initCanvas: (
    workflowId: string,
    nodes: WorkflowNode[],
    edges: WorkflowEdge[],
    schemaVersion: number,
  ) => void;
  resetCanvas: () => void;
  onNodesChange: (changes: NodeChange<WorkflowNode>[]) => void;
  onEdgesChange: (changes: EdgeChange[]) => void;
  onConnect: (connection: Connection) => void;
  addNode: (node: WorkflowNode) => void;
  removeNode: (id: string) => void;
  selectNode: (id: string | null) => void;
  setDirty: (dirty: boolean) => void;
}

export const useWorkflowStore = create<WorkflowStoreState>()(
  temporal(
    (set) => ({
      workflowId: null,
      nodes: [],
      edges: [],
      selectedNodeId: null,
      schemaVersion: 1,
      isDirty: false,

      initCanvas: (workflowId, nodes, edges, schemaVersion) =>
        set({ workflowId, nodes, edges, schemaVersion, isDirty: false, selectedNodeId: null }),

      resetCanvas: () =>
        set({
          workflowId: null,
          nodes: [],
          edges: [],
          selectedNodeId: null,
          schemaVersion: 1,
          isDirty: false,
        }),

      onNodesChange: (changes) =>
        set((s) => ({
          nodes: applyNodeChanges(changes, s.nodes),
          isDirty: true,
        })),

      onEdgesChange: (changes) =>
        set((s) => ({
          edges: applyEdgeChanges(changes, s.edges),
          isDirty: true,
        })),

      onConnect: (connection) =>
        set((s) => ({
          edges: addEdge({ ...connection, animated: false }, s.edges),
          isDirty: true,
        })),

      addNode: (node) => set((s) => ({ nodes: [...s.nodes, node], isDirty: true })),

      removeNode: (id) =>
        set((s) => ({
          nodes: s.nodes.filter((n) => n.id !== id),
          edges: s.edges.filter((e) => e.source !== id && e.target !== id),
          selectedNodeId: s.selectedNodeId === id ? null : s.selectedNodeId,
          isDirty: true,
        })),

      selectNode: (id) => set({ selectedNodeId: id }),

      setDirty: (dirty) => set({ isDirty: dirty }),
    }),
    {
      partialize: (state) => ({
        nodes: state.nodes,
        edges: state.edges,
      }),
    },
  ),
);
